import {
  ExceptionErrorCode,
  FinancingConfigDto,
  ProvinceConfigs,
  reportErrorToServer,
  useFeatureFlags,
  useGetCreditApplicationById,
} from '@applyonline/shared'
import { AcceptTermsCheckbox, AsyncActionButton, ScrollableAlert } from '@applyonline/shared/components'
import { EFinancingProgram } from '@if/core/types'
import { ValidationError } from '@if/core/yup'
import { AxiosError } from 'axios'
import { useState } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { SubmitCoapplicantDto, useSubmitCoapplicant } from '../../../api/credit-api'
import { getRecaptchaToken } from '../../../services/recaptcha'
import { Applicant, ApplicantSchema } from '../../../types/Schemas/ApplicantSchema'
import { Prequalification } from '../../../types/Schemas/PrequalificationSchema'
import ReviewApplicantInfo from './ReviewApplicantInfo'

interface Props {
  gotoStep: (step: string) => void
  prequalification: Prequalification
  creditApplicationId: string
  provinceConfigs: ProvinceConfigs
  financingProgramConfig: FinancingConfigDto
}

const ReviewCoapplicant = ({ prequalification, gotoStep, creditApplicationId }: Props) => {
  const { t } = useTranslation()
  const { executeRecaptcha } = useGoogleReCaptcha()
  const navigate = useNavigate()
  const [hasApplicantError, setHasApplicantError] = useState<boolean>(false)
  const [hasApplicantAddressError, setHasApplicantAddressError] = useState<boolean>(false)
  const [hasApplicantRevenueError, setHasApplicantRevenueError] = useState<boolean>(false)
  const [hasFormError, setHasFormError] = useState<boolean>(false)
  const [hasUnexpectedError, setHasUnexpectedError] = useState<boolean>(false)
  const [isHandlerRunning, setIsHandlerRunning] = useState(false)
  const [submitCoapplicant, isSubmitting] = useSubmitCoapplicant()

  const [creditApp, isFetchingCreditApp] = useGetCreditApplicationById(
    creditApplicationId,
    EFinancingProgram.Personal,
    false,
  )
  const isCreditAppPrequalified = creditApp?.normsDecision !== null
  const [featureFlags] = useFeatureFlags(EFinancingProgram.Personal)
  // Ticket #67167 - gate the merged consent (#65379 / PR 16576) behind a feature switch; off = legacy separate consents.
  const useNewConsentFlow = featureFlags?.useNewApplyConsentFlow ?? false

  const applicant = prequalification.applicant
  const validateCoapplicantData = async (): Promise<Applicant | null> => {
    setHasApplicantError(false)
    setHasApplicantAddressError(false)
    setHasApplicantRevenueError(false)
    setHasFormError(false)
    let formHasError = false
    let validated: Applicant | null = null
    try {
      validated = await ApplicantSchema().validate(applicant, {
        abortEarly: false,
      })
    } catch (error) {
      formHasError = true
      if (error instanceof ValidationError) {
        error.inner.forEach((err) => {
          const path = err.path ?? ''
          if (path.startsWith('currentAddress') || path.startsWith('spending')) {
            setHasApplicantAddressError(true)
          } else if (path.startsWith('currentJobs') || path.startsWith('otherIncomes')) {
            setHasApplicantRevenueError(true)
          } else {
            setHasApplicantError(true)
          }
        })
      } else {
        reportErrorToServer(error as Error)
      }
    }
    if (formHasError) {
      setHasFormError(formHasError)
      window.scrollTo(0, 0)
      return null
    }
    return validated
  }

  const buildDtoAndSubmitCoapplicant = async (evt: { preventDefault: () => void }) => {
    evt.preventDefault()
    if (isHandlerRunning) return
    setIsHandlerRunning(true)
    setHasUnexpectedError(false)
    try {
      const validatedCoapplicant = await validateCoapplicantData()
      if (validatedCoapplicant) {
        try {
          const recaptchaToken = await getRecaptchaToken(executeRecaptcha, 'submit_postCreditApplicationAndPrequalify')
          const dto: SubmitCoapplicantDto = {
            recaptchaToken,
            coapplicant: validatedCoapplicant,
            creditApplicationId,
          }
          await submitCoapplicant(dto)
          navigate(`/creditApplication/${creditApplicationId}`, { replace: true })
        } catch (error) {
          const axiosError = error as AxiosError<{ errorCode: string }>
          const errorCode = axiosError.response?.data?.errorCode
          if (axiosError.response?.status === 422 && errorCode === ExceptionErrorCode.RecaptchaException) {
            navigate('/recaptcha-validation-failed')
          } else {
            setHasUnexpectedError(true)
            window.scrollTo(0, 0)
            reportErrorToServer(error as Error)
          }
        }
      }
    } finally {
      setIsHandlerRunning(false)
    }
  }

  return (
    <section className="step-content">
      <h3 className="form-question">{t('review.title')}</h3>
      <div className="paragraph">
        <p>{t('review.description')}</p>
      </div>

      <form onSubmit={buildDtoAndSubmitCoapplicant}>
        <ScrollableAlert showAlert={hasFormError} type="error" message={t('review.formContainsErrors')} />
        <ScrollableAlert showAlert={hasUnexpectedError} type="error" message={t('common.unexpectedError')} />
        <div className="review-wrap">
          <ReviewApplicantInfo
            applicant={applicant}
            hasApplicantError={hasApplicantError}
            hasApplicantAddressError={hasApplicantAddressError}
            hasApplicantRevenueError={hasApplicantRevenueError}
            gotoStep={gotoStep}
          />
        </div>

        <footer className="review-footer">
          {useNewConsentFlow ? (
            <>
              <AcceptTermsCheckbox
                msgOnInvalid={t('common.acceptConditions')}
                id="acceptConditions"
              >
                <strong>{t('confirmation.consent.title')}</strong>
                <br />
                {t('confirmation.consent.text')}
              </AcceptTermsCheckbox>

              <AcceptTermsCheckbox
                msgOnInvalid={t('common.acceptConditions')}
                id="acceptAuthorise"
              >
                <strong>{t('confirmation.authorization.title')}</strong>
                <br />
                {t('confirmation.authorization.text')}
                <u>
                  <a href={t('common.policyLink')} target="_blank" rel="noreferrer">
                    {t('contact.linkText')}
                  </a>
                </u>
              </AcceptTermsCheckbox>
            </>
          ) : (
            <>
              <h3>
                <Trans i18nKey={isCreditAppPrequalified ? 'prequalification.pullCreditReport' : 'confirmation.consentTitle'} />
              </h3>
              {isCreditAppPrequalified ? (
                <AcceptTermsCheckbox
                  msgOnInvalid={t('common.acceptConditions')}
                  id="acceptConditions"
                  termId={` ${String(t('prequalification.acceptConditions'))}¹`}
                />
              ) : (
                <AcceptTermsCheckbox
                  msgOnInvalid={t('common.acceptConditions')}
                  id="acceptConditions"
                  termId={t('confirmation.consentText')}
                />
              )}

              <AcceptTermsCheckbox
                msgOnInvalid={t('common.acceptConditions')}
                id="acceptAuthorise"
                termId={t('common.acceptAuthorization')}
              >
                <br />
                {t('confirmation.authorizationStart')}
                <u>
                  <a href={t('common.policyLink')} target="_blank" rel="noreferrer">
                    {t('contact.linkText')}
                  </a>
                </u>
                {t('confirmation.authorizationEnd')}
              </AcceptTermsCheckbox>
            </>
          )}
          <AsyncActionButton
            type="submit"
            className="btn btn-blue"
            disabled={isHandlerRunning || isSubmitting || hasFormError}
            isPending={isHandlerRunning || isSubmitting}
            primary
          >
            <span>{t('review.getQuote')}</span>
            <i className="fa-regular fa-arrow-right" />
          </AsyncActionButton>

          {isCreditAppPrequalified && !isFetchingCreditApp && (
            <div style={{ borderTop: '1px solid black', marginTop: '5rem' }}>
              <p style={{ marginTop: '2rem' }}>¹{t('prequalification.priorToQualifyNotes.creditReport')}</p>
            </div>
          )}
        </footer>
      </form>
    </section>
  )
}

export default ReviewCoapplicant
